import React, { useState, useEffect, useRef, useCallback } from "react";
import { useFormContext, get, useController, useFormState } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { classNames } from "helpers/commonHelpers";
import { useReviewFieldErrorClear } from "@RequestFormV2/hooks/useReviewFieldErrorClear";
import { ReviewFieldErrorClearWatcher } from "@RequestFormV2/hooks/ReviewFieldErrorClearWatcher";
import "./TimeInput.scss";

type TimeInputProps = {
  name: string;
  defaultValue?: string;
  required?: boolean;
  validations?: object;
  outputFormat?: string;
  registerOptions?: object;
  shouldUnregister?: boolean;
  onTimeSelect?: (time?: string) => void;
  onComplete?: () => void;
  disabled?: boolean;
  label?: string;
  errorMessage?: string;
  testId?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  /** Skip RHF trigger after a complete entry — use for optional fields with relational validators. */
  skipTriggerOnComplete?: boolean;
};

const PLACEHOLDER_TEMPLATE = "HH:mm";

function applyTimeMask(digits: string, isDeleting: boolean): string {
  const d = digits.slice(0, 4);
  let result = "";
  for (let i = 0; i < d.length; i += 1) {
    if (i === 2) result += ":";
    result += d[i];
  }
  if (!isDeleting && d.length === 2) result += ":";
  return result;
}

function getGhostText(currentInput: string): { invisible: string; visible: string } {
  return {
    invisible: currentInput,
    visible: PLACEHOLDER_TEMPLATE.slice(currentInput.length),
  };
}

function parseStoredValue(val: string): string {
  if (!val) return "";
  const digits = val.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 4) {
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  }
  return "";
}

function TimeInput(props: TimeInputProps) {
  const {
    name,
    defaultValue,
    required,
    validations,
    registerOptions,
    outputFormat = "HH:mm:ss",
    shouldUnregister,
    disabled,
    label,
    errorMessage,
    testId,
    inputRef,
    onTimeSelect = () => {},
    onComplete,
    skipTriggerOnComplete = false,
  } = props;

  const intl = useIntl();
  const [inputValue, setInputValue] = useState<string>("");
  const [localError, setLocalError] = useState<string | undefined>();
  const isDeletingRef = useRef(false);
  // Holds the current digit count for handleBlur. A ref (not state) so it stays
  // correct even when blur fires mid-change (e.g. onComplete shifts focus while
  // the final digit is still being typed and a state closure would be stale).
  const rawDigitsRef = useRef<string>("");
  const lastCommittedRef = useRef<string>("");
  const clearReviewError = useReviewFieldErrorClear(name, { isTime: true });

  const {
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext();

  // Subscribe to this field's errors — parent formState alone may not re-render TimeInput.
  useFormState({ control, name, exact: true });

  const { field } = useController({
    name,
    control,
    defaultValue,
    shouldUnregister,
    rules: {
      required,
      validate: {
        _validTime: (val) => {
          if (!val) return true;
          const digits = val.replace(/\D/g, "");
          if (digits.length < 4) return true;
          const h = parseInt(digits.slice(0, 2), 10);
          const m = parseInt(digits.slice(2, 4), 10);
          if (h > 23 || m > 59) return intl.formatMessage({ id: "general.validTimeError", defaultMessage: "Enter a valid time" });
          return true;
        },
        ...validations,
      },
      ...registerOptions,
    },
  });

  useEffect(() => {
    const formValue = getValues(name);
    if (formValue) {
      const display = parseStoredValue(formValue);
      if (display) {
        setInputValue(display);
        rawDigitsRef.current = display.replace(/\D/g, "");
        lastCommittedRef.current = display;
        setLocalError(undefined);
      }
    } else {
      setInputValue("");
      rawDigitsRef.current = "";
      lastCommittedRef.current = "";
      setLocalError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues(name)]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    isDeletingRef.current = e.key === "Backspace" || e.key === "Delete";
  };

  const validTimeError = intl.formatMessage({ id: "general.validTimeError", defaultMessage: "Enter a valid time" });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
    rawDigitsRef.current = digits;
    const masked = applyTimeMask(digits, isDeletingRef.current);
    setInputValue(masked);
    setLocalError(undefined);

    if (digits.length === 4) {
      const h = parseInt(digits.slice(0, 2), 10);
      const m = parseInt(digits.slice(2, 4), 10);
      const formatted =
        outputFormat === "HH:mm" ? `${digits.slice(0, 2)}:${digits.slice(2, 4)}` : `${digits.slice(0, 2)}:${digits.slice(2, 4)}:00`;
      field.onChange(formatted);

      const finish = () => {
        onTimeSelect(formatted);
        if (h <= 23 && m <= 59) {
          lastCommittedRef.current = masked;
          clearReviewError(formatted);
          onComplete?.();
        } else {
          setLocalError(validTimeError);
        }
      };

      if (skipTriggerOnComplete) {
        queueMicrotask(finish);
      } else {
        trigger(name).then(finish);
      }
    } else if (digits.length === 0) {
      field.onChange("");
      lastCommittedRef.current = "";
      trigger(name).then(() => onTimeSelect());
    }
  };

  const handleBlur = () => {
    field.onBlur();
  };

  const mergedRef = useCallback(
    (node: HTMLInputElement | null) => {
      field.ref(node);
      if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [field, inputRef],
  );

  const fieldError = get(errors, name);
  const resolvedError = localError || errorMessage || fieldError?.message;
  const hasError = !!(localError || errorMessage || fieldError);
  const resolvedTestId = testId || `form-field-${name.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`;

  return (
    <>
      <ReviewFieldErrorClearWatcher fieldPath={name} options={{ isTime: true }} />
      <div className="time-input-v2">
      {label && (
        <label className={classNames("time-input-v2__label", { required })}>{label}</label>
      )}
      <div className={`time-input-v2__wrapper${hasError ? " error" : ""}${disabled ? " disabled" : ""}`}>
        <div className="time-input-v2__field">
          <input
            type="text"
            inputMode="numeric"
            className="time-input-v2__input"
            data-testid={resolvedTestId}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder=""
            disabled={disabled}
            ref={mergedRef}
            name={field.name}
          />
          {!disabled && (
            <span className="time-input-v2__ghost" aria-hidden="true">
              <span className="time-input-v2__ghost--invisible">{getGhostText(inputValue).invisible}</span>
              <span className="time-input-v2__ghost--visible">{getGhostText(inputValue).visible}</span>
            </span>
          )}
        </div>
      </div>
      {hasError && (
        <div className="text-sm time-input-v2__error">
          {resolvedError || <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />}
        </div>
      )}
    </div>
    </>
  );
}

export default TimeInput;

TimeInput.defaultProps = {
  required: false,
  validations: undefined,
  defaultValue: undefined,
  registerOptions: undefined,
  outputFormat: "HH:mm:ss",
  onTimeSelect: undefined,
  shouldUnregister: false,
  disabled: false,
  label: undefined,
  errorMessage: undefined,
  testId: undefined,
  onComplete: undefined,
  inputRef: undefined,
  skipTriggerOnComplete: false,
};
